import type { CelebrationRow } from "@/hooks/queries/useCelebrations";
import type { ApprovedCelebrationRow } from "@/hooks/queries/useCelebrations";
import type { OrderListRow } from "@/hooks/queries/useOrders";

export type DashboardListFilter =
  | "all"
  | "awaiting"
  | "autopilot"
  | "needs_checkout"
  | "skipped";

/** Migrate persisted filter keys from the old "locked_in" label era. */
export function normalizeDashboardFilter(raw: string): DashboardListFilter | null {
  const key = raw === "locked_in" ? "needs_checkout" : raw;
  if (
    key === "all" ||
    key === "awaiting" ||
    key === "autopilot" ||
    key === "needs_checkout" ||
    key === "skipped"
  ) {
    return key;
  }
  return null;
}

/** Approved celebration with no order row yet — checkout still required. */
export function isNeedsCheckoutCelebration(c: Pick<CelebrationRow, "status">): boolean {
  return c.status === "approved";
}

/** Stripe or gateway failure — the only "unpaid" state we surface on Home. */
export function isPaymentFailedOrder(
  order: Pick<OrderListRow, "payment_status">,
): boolean {
  return (order.payment_status ?? "").toLowerCase() === "failed";
}

/** Abandoned checkout draft on Orders only (pending + not paid). Not shown on Home. */
export function isAbandonedCheckoutOrder(
  order: Pick<OrderListRow, "status" | "payment_status">,
): boolean {
  return (
    order.status === "pending" &&
    (order.payment_status ?? "unpaid").toLowerCase() !== "paid" &&
    !isPaymentFailedOrder(order)
  );
}

export function approvedCelebrationToRow(cel: ApprovedCelebrationRow): CelebrationRow {
  return {
    id: cel.id,
    date: cel.date,
    type: cel.type,
    // Query is `.eq("status", "approved")`; row type omits status but every
    // mapped row is approved pending checkout.
    status: cel.status ?? "approved",
    team_members: cel.team_members,
  };
}
